'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  planName?: string;
  isMultiple?: boolean;
  count?: number;
}

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  planName, 
  isMultiple = false, 
  count = 1 
}: DeleteConfirmationModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isHydrated]);

  if (!isHydrated || !isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-error" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {isMultiple ? 'Delete Multiple Plans' : 'Delete Plan'}
          </h3>
        </div>

        <div className="mb-6">
          <p className="text-muted-foreground">
            {isMultiple ? (
              <>Are you sure you want to delete {count} selected plans? This action cannot be undone.</>
            ) : (
              <>Are you sure you want to delete "{planName}"? This action cannot be undone and all associated data will be permanently removed.</>
            )}
          </p>
        </div>

        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium bg-error text-error-foreground rounded-md hover:bg-error/90 transition-colors duration-200"
          >
            Delete {isMultiple ? 'Plans' : 'Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;